import React, { useState } from 'react';
import classes from 'classnames';
import { v4 as uuid } from 'uuid';
import { Typography } from '../index';
import Input from '../Input/Input'; // Assuming the Input component is in the same folder
import styles from './SelectWithDescriptions.module.scss';

export type SelectDescType = {
  name: string;
  desc: string;
  value: string;
};

type AutocompleteProps = {
  options?: SelectDescType[]; // Optional static list of options
  placeholder?: string; // Placeholder text for the input field
  onOptionSelect?: (option: SelectDescType) => void; // Callback when an option is selected
  dropdownItemClassName?: string;
  className?: string;
  dataTest?: string;
  withSearchIcon?: boolean;
  optionSelected?: SelectDescType;
  disabled?: boolean;
};

const renderOption = (option: SelectDescType) => {
  return (
    <div className={styles['dropdown-company-info']}>
      <span>{option.name}</span>
      <Typography
        color="muted"
        dataTest="EducationEmployment-identificationNumber"
      >
        {option.desc}
      </Typography>
    </div>
  );
};

export default function SelectWithDescriptions({
  options = [],
  placeholder = '',
  onOptionSelect,
  dropdownItemClassName,
  className,
  dataTest,
  withSearchIcon = false,
  optionSelected,
  disabled = false,
}: AutocompleteProps) {
  const [showList, setShowList] = useState<boolean>(false);

  // Handle selection of an option
  const handleSelect = (option: SelectDescType | undefined) => {
    setShowList(false);
    if (onOptionSelect && option) {
      onOptionSelect(option); // Notify parent about the selected option
    }
  };

  const handleKeyDown = (
    event: React.KeyboardEvent<HTMLDivElement>,
    option: SelectDescType,
  ) => {
    if (event.key === 'Enter') {
      handleSelect(option);
    }
  };

  return (
    <div className={classes([styles.wrapper, className])} data-test={dataTest}>
      <Input
        data-test="autocomplete-input"
        value={optionSelected?.name}
        type="text"
        onChange={(event) =>
          handleSelect(
            options.find((option) => option.value === event.target.value),
          )
        }
        onClick={() => setShowList(true)}
        placeholder={placeholder}
        className={styles.input}
        withSearchIcon={withSearchIcon}
        disabled={disabled}
        dataTest={`${dataTest}-input`}
        isTouched={false}
      />
      {/* Dropdown for displaying results */}
      {options.length > 0 && showList && (
        <ul
          className={styles.dropdown}
          key={uuid()}
          data-test={`${dataTest}-list`}
        >
          {options.map((option, index) => (
            <div
              key={index}
              onClick={() => handleSelect(option)}
              onKeyDown={(e) => handleKeyDown(e, option)}
              data-test={`${dataTest}-option-${index}`}
            >
              <li
                className={classes(
                  styles['dropdown-item'],
                  dropdownItemClassName,
                )}
                data-test={`${dataTest}-option-${index}-item`}
              >
                {renderOption(option)}
              </li>
            </div>
          ))}
        </ul>
      )}
    </div>
  );
}
